import { type ExecutionDetail, type ExecutionSummary, pipelineStopLine } from '@/api'

/**
 * How an execution a task stopped itself (`os.stop_execution`, #810) reads on the board and on its detail page:
 * the task and its reason, e.g. "Stopped by eng-dry: pr_merged". Null for every other execution, an operator's
 * cancel included, which keeps reading as the cancel it is. One function, so the list and the detail page cannot
 * word it differently.
 */
export function executionStoppedNote(
    execution: Pick<ExecutionSummary, 'cancelledBy' | 'cancelledReason'>,
): string | null {
    const reason = execution.cancelledReason
    if (!reason) return null
    return execution.cancelledBy ? `Stopped by ${execution.cancelledBy.name}: ${reason}` : `Stopped: ${reason}`
}

/**
 * What a person's pause does (#809), said beside who paused it on the board and the detail page alike: "Paused" alone
 * reads as if everything stopped, while a step already running carries on to its end.
 */
export const EXECUTION_PAUSED_HOLD = 'A step already running finishes. Nothing new starts until someone resumes it.'

/**
 * Why a chain that stopped by itself (`stoppedReason`) needs attention, in words: the task whose run stopped it, and
 * a dead end said as that run's own card says it (`pipelineStopLine`) rather than as its recorded code. Any other
 * reason is shown as it came. Null when nothing stopped the chain.
 */
export function executionAttentionNote(
    execution: Pick<ExecutionDetail, 'stoppedReason' | 'stoppedRunId' | 'runs'>,
): string | null {
    const reason = execution.stoppedReason
    if (!reason) return null
    const words = pipelineStopLine(reason) ?? reason
    const task = execution.runs.find(run => run.id === execution.stoppedRunId)?.task.name
    return task ? `${task}: ${words}` : words
}
